/* dd-video-shelf.js
   Behavior for the vertical video shelf rendered inside dd-reviews.
   Single delegated listener on document handles every shelf on the
   page (typically one per PDP, but the pattern is resilient if
   multiple ever appear).

   1. Click-to-play: click on tile or play button hides the cover,
      unmutes, plays, and shows native controls so the viewer can
      pause/scrub from inside the tile.
   2. Single-audio rule: starting one shelf video pauses every other
      shelf video on the page.
   3. Overflow state: each shelf root gets `is--can-prev` /
      `is--can-next` classes reflecting whether content remains
      offscreen in that direction. CSS uses the same classes for the
      edge-fade mask (all viewports) and the arrow buttons (desktop),
      so the affordances can never show without JS alive to update
      them.
   4. Arrow nav: prev/next buttons scroll by roughly two tiles;
      scroll-snap settles the landing position. */

(function () {
  if (window._ddVideoShelfInit) return;
  window._ddVideoShelfInit = true;

  /* ── Click-to-play ─────────────────────────────────────────── */

  function activateTile(tile) {
    if (!tile) return;
    var video = tile.querySelector('.dd-video-shelf__video');
    var cover = tile.querySelector('.dd-video-shelf__cover');
    if (!video) return;
    if (cover) cover.classList.add('is-hidden');
    video.controls = true;
    video.muted = false;
    try {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* Autoplay can fail on first interaction in some Safari
             configs even with user gesture (e.g. low-power mode).
             Fall back to muted playback so SOMETHING happens -
             the cover already faded so the viewer has feedback. */
          video.muted = true;
          video.play().catch(function () { /* give up */ });
        });
      }
    } catch (_) { /* defensive: never let video errors break the page */ }
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    /* Arrow nav first: the buttons live inside the shelf root but
       outside any tile, so the two branches never overlap. */
    var nav = e.target.closest('[data-dd-shelf-nav]');
    if (nav) {
      var shelf = nav.closest('[data-dd-video-shelf]');
      var scroller = shelf && shelf.querySelector('.dd-video-shelf__scroller');
      if (!scroller) return;
      var tile = scroller.querySelector('.dd-video-shelf__tile');
      var step = tile ? (tile.offsetWidth + 16) * 2 : scroller.clientWidth * 0.8;
      var dir = nav.getAttribute('data-dd-shelf-nav') === 'prev' ? -1 : 1;
      scroller.scrollBy({ left: step * dir, behavior: 'smooth' });
      return;
    }

    var clickedTile = e.target.closest('.dd-video-shelf__tile');
    if (!clickedTile) return;
    if (clickedTile.classList.contains('dd-video-shelf__tile--placeholder')) return;
    /* If the user clicked the native video controls (visible after
       activation), let the browser handle it - don't re-activate. */
    var cover = clickedTile.querySelector('.dd-video-shelf__cover');
    if (!cover || cover.classList.contains('is-hidden')) return;
    e.preventDefault();
    activateTile(clickedTile);
  });

  /* Single-audio rule: when one shelf video starts playing, pause
     every other shelf video on the page. Listening on the play
     event covers both our activate() path and the native controls
     play button after activation. */
  document.addEventListener(
    'play',
    function (e) {
      var v = e.target;
      if (!v || !v.classList || !v.classList.contains('dd-video-shelf__video')) return;
      var others = document.querySelectorAll('.dd-video-shelf__video');
      for (var i = 0; i < others.length; i++) {
        if (others[i] !== v && !others[i].paused) {
          try { others[i].pause(); } catch (_) {}
        }
      }
    },
    true /* capture - 'play' doesn't bubble, must intercept on the way down */
  );

  /* ── Overflow state (edge fades + arrows) ──────────────────── */

  function updateShelfState(shelf, scroller) {
    /* 2px slack absorbs subpixel scrollLeft values on zoomed /
       high-DPI layouts so the end states latch cleanly. */
    var max = scroller.scrollWidth - scroller.clientWidth;
    var x = scroller.scrollLeft;
    shelf.classList.toggle('is--can-prev', x > 2);
    shelf.classList.toggle('is--can-next', max - x > 2);
  }

  function initShelf(shelf) {
    if (shelf._ddShelfStateInit) return;
    shelf._ddShelfStateInit = true;
    var scroller = shelf.querySelector('.dd-video-shelf__scroller');
    if (!scroller) return;

    var raf = null;
    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        updateShelfState(shelf, scroller);
      });
    }

    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    updateShelfState(shelf, scroller);
  }

  function initAll() {
    var shelves = document.querySelectorAll('[data-dd-video-shelf]');
    for (var i = 0; i < shelves.length; i++) initShelf(shelves[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
